// src/components/Admin/SellerManagement/SellerCommsHistorySection.jsx
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  History,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  MessageSquare,
  Bell,
  Phone,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Skeleton } from "../../../components/ui/skeleton";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";

import { getSellerCommsHistory } from "../../../services/AdminApis";

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatTs = (ts) => (ts ? format(new Date(ts), "dd MMM yyyy, HH:mm") : "N/A");

const CHANNEL_ICONS = {
  EMAIL: Mail,
  SMS: MessageSquare,
  WHATSAPP: MessageSquare,
  PUSH: Bell,
  PHONE_CALL: Phone,
};

const ChannelTag = ({ channel }) => {
  const Icon = CHANNEL_ICONS[channel] || MessageSquare;
  const isWhatsapp = channel === "WHATSAPP";
  return (
    <span
      className={
        isWhatsapp
          ? "inline-flex items-center gap-1 rounded-full bg-[#25D366]/10 px-2 py-0.5 text-xs font-semibold text-[#25D366]"
          : "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-foreground"
      }
    >
      <Icon className="h-3 w-3" />
      {channel}
    </span>
  );
};

const StatusBadge = ({ status, errorMessage }) => {
  const s = (status || "").toLowerCase();

  if (s === "sent") {
    return (
      <Badge variant="outline" className="gap-1 text-xs font-normal text-brand-growth-green border-brand-growth-green/40">
        <CheckCircle2 className="h-3 w-3" />
        Sent
      </Badge>
    );
  }
  if (s === "failed") {
    return (
      <Badge variant="destructive" className="gap-1 text-xs font-normal" title={errorMessage || undefined}>
        <XCircle className="h-3 w-3" />
        Failed
      </Badge>
    );
  }
  if (s === "pending") {
    return (
      <Badge variant="secondary" className="gap-1 text-xs font-normal">
        <Clock className="h-3 w-3" />
        Pending
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs font-normal">
      {status || "Unknown"}
    </Badge>
  );
};

// ── Loading skeleton ──────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-2">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center justify-between gap-3 rounded-md bg-muted/50 p-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-5 w-28" />
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const SellerCommsHistorySection = ({ sellerProfileId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch only on first expand - never as part of the page's eager initial load.
  useEffect(() => {
    if (!isOpen || hasFetched || !sellerProfileId) return;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      setLogs([]);
      try {
        const data = await getSellerCommsHistory(sellerProfileId);
        setLogs(data?.logs ?? []);
      } catch (err) {
        console.error("SellerCommsHistorySection: failed to fetch comms history", err);
        setError(err);
      } finally {
        setIsLoading(false);
        setHasFetched(true);
      }
    };

    fetchHistory();
  }, [isOpen, hasFetched, sellerProfileId]);

  const handleRetry = () => {
    setError(null);
    setHasFetched(false);
  };

  const status = error?.response?.status;
  const isNotFound = status === 404;
  const isForbidden = status === 403;

  let errorMessage = null;
  if (error && !isNotFound) {
    if (isForbidden) {
      errorMessage = "You don't have permission to view this seller's communication history.";
    } else {
      errorMessage = "Failed to load communication history. Please try again.";
    }
  }

  return (
    <Card>
      {/* Clickable header toggles the section - history is only fetched after this is opened */}
      <CardHeader
        className="cursor-pointer select-none hover:bg-muted/30 transition-colors rounded-t-xl"
        onClick={() => setIsOpen((v) => !v)}
        role="button"
        aria-expanded={isOpen}
      >
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-5 w-5 text-primary" />
          Communication History
          {hasFetched && !error && (
            <Badge variant="outline" className="text-xs font-normal">
              {logs.length} {logs.length === 1 ? "message" : "messages"}
            </Badge>
          )}
          <span className="ml-auto">
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </span>
        </CardTitle>
        {!isOpen && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Click to view the last 20 logged communications
          </p>
        )}
      </CardHeader>

      {isOpen && (
        <CardContent className="pt-0 space-y-2">
          {isLoading && <LoadingSkeleton />}

          {!isLoading && errorMessage && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>{errorMessage}</span>
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !errorMessage && hasFetched && (isNotFound || logs.length === 0) && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No communications recorded for this seller.
            </p>
          )}

          {!isLoading &&
            !errorMessage &&
            !isNotFound &&
            logs.map((log) => (
              <div
                key={log.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm p-2 rounded-md bg-muted/50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ChannelTag channel={log.channel} />
                  {log.template_id && (
                    <span className="text-xs font-mono text-muted-foreground truncate">
                      {log.template_id}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground truncate">
                    to {log.sent_to}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={log.status} errorMessage={log.error_message} />
                  <span className="text-xs text-muted-foreground">{formatTs(log.created_at)}</span>
                </div>
              </div>
            ))}
        </CardContent>
      )}
    </Card>
  );
};

export default SellerCommsHistorySection;
